import type { ReactNode } from "react";
import { Button } from "./Button";
import "./AsyncStates.css";

export function Spinner({ label = "Loading" }: { label?: string }) {
  return (
    <span className="spinner" role="status">
      <svg className="spinner__ring" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="3" opacity="0.25" />
        <path d="M22 12a10 10 0 0 0-10-10" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      </svg>
      <span className="visually-hidden">{label}</span>
    </span>
  );
}

export function LoadingState({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="async-state" role="status">
      <Spinner label={label} />
      <p className="async-state__text">{label}</p>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="async-state async-state--empty">
      <p className="async-state__title">{title}</p>
      {description && <p className="async-state__text">{description}</p>}
      {action}
    </div>
  );
}

export function ErrorState({
  title = "Something went wrong",
  description,
  onRetry,
  action,
}: {
  title?: string;
  description: string;
  onRetry?: () => void;
  /** An escape hatch out of a dead end — e.g. a "not found" state whose copy
   * already names where to go instead (back to the list, the parent
   * resource, etc). Rendered alongside `onRetry`'s "Try again" when both are
   * given, since a truly-gone resource still benefits from a way out even if
   * a caller also wires up a (harmless) retry. */
  action?: ReactNode;
}) {
  return (
    <div className="async-state async-state--error" role="alert">
      <p className="async-state__title">{title}</p>
      <p className="async-state__text">{description}</p>
      {onRetry && (
        <Button variant="secondary" onClick={onRetry}>
          Try again
        </Button>
      )}
      {action}
    </div>
  );
}
